/**
 * SseTransport — SSE-based Parlant event transport.
 *
 * Uses @microsoft/fetch-event-source so Authorization headers work with
 * Bearer tokens (native EventSource cannot send custom headers).
 *
 * Behaviour:
 *  - Opens one persistent SSE stream on /sessions/{id}/events?sse=true.
 *  - For each incoming event whose data.chunks is present but not yet
 *    terminated (last chunk !== null), opens a per-message SSE stream on
 *    /sessions/{id}/events/{eventId}?sse=true via ConnectionGovernor.
 *  - On list-events stream error, reconnects with exponential backoff + full
 *    jitter.  Resets backoff after a successful open.
 *  - Respects browser "visibilitychange" (visible → immediate reconnect) and
 *    "online" events (both handled via BackoffPolicy.reset() on the online
 *    event listener registered in BackoffPolicy itself, and an explicit
 *    reconnect trigger here).
 *  - Emits metrics: ttfc (time-to-first-chunk, ms), reconnect (attempt number).
 */

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { BaseTransport } from './ChatTransport';
import { BackoffPolicy } from './BackoffPolicy';
import { ConnectionGovernor } from './ConnectionGovernor';
import { isMessageStreaming } from '../types/parlant';
import type { ParlantEvent } from '../types/parlant';
import { normalizeParlantEvent } from '../utils/parlantEvent';

export interface SseTransportOptions {
  serverUrl: string;
  authToken?: string;
  /** Seconds to wait between events before the server closes the stream (default 60). */
  waitForData?: number;
}

export class SseTransport extends BaseTransport {
  private readonly serverUrl: string;
  private readonly authToken?: string;
  private readonly waitForData: number;

  private sessionId: string | null = null;
  private lastOffset = 0;

  private listAbortController: AbortController | null = null;
  private backoff: BackoffPolicy;
  private governor: ConnectionGovernor;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  private visibilityListener: (() => void) | null = null;
  private onlineListener: (() => void) | null = null;

  constructor(opts: SseTransportOptions) {
    super();
    this.serverUrl = opts.serverUrl.endsWith('/')
      ? opts.serverUrl.slice(0, -1)
      : opts.serverUrl;
    this.authToken = opts.authToken;
    this.waitForData = opts.waitForData ?? 60;
    this.backoff = new BackoffPolicy();
    this.governor = new ConnectionGovernor();

    if (typeof document !== 'undefined') {
      this.visibilityListener = () => {
        if (document.visibilityState === 'visible' && !this.stopped) {
          this.backoff.reset();
          this._reconnect();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityListener);
    }

    if (typeof window !== 'undefined') {
      this.onlineListener = () => {
        if (!this.stopped) {
          this.backoff.reset();
          this._reconnect();
        }
      };
      window.addEventListener('online', this.onlineListener);
    }
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  override start(sessionId: string, lastOffset: number): void {
    this.stopped = false;

    // New session resets everything
    if (sessionId !== this.sessionId) {
      this.governor.cancelAll();
    }

    this.sessionId = sessionId;
    this.lastOffset = lastOffset;
    this.backoff.reset();
    this._reconnect();
  }

  override stop(): void {
    this.stopped = true;
    this._clearReconnectTimer();
    this.listAbortController?.abort();
    this.listAbortController = null;
    this.governor.cancelAll();

    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
    this.backoff.destroy();
  }

  // -------------------------------------------------------------------------
  // List-events stream
  // -------------------------------------------------------------------------

  private _reconnect(): void {
    if (this.stopped || !this.sessionId) return;

    this._clearReconnectTimer();
    this.listAbortController?.abort();
    this.listAbortController = new AbortController();

    const sessionId = this.sessionId;
    const url = this._listEventsUrl(sessionId, this.lastOffset);
    const headers = this._headers();

    const startedAt = Date.now();
    let firstEvent = true;

    fetchEventSource(url, {
      headers,
      signal: this.listAbortController.signal,

      onopen: async (response) => {
        if (response.ok) {
          this.backoff.reset();
          return;
        }
        // Non-2xx on open = fatal (e.g. 401, 404); don't retry automatically
        const msg = `SSE list_events open failed: ${response.status}`;
        this.emit('error', msg);
        this.listAbortController?.abort();
        throw new Error(msg);
      },

      onmessage: (ev) => {
        if (!ev.data) return;
        try {
          const raw = JSON.parse(ev.data) as Record<string, unknown>;
          const event = normalizeParlantEvent(raw);

          // Advance offset
          if (typeof event.offset === 'number') {
            this.lastOffset = Math.max(this.lastOffset, event.offset + 1);
          }

          // Measure time-to-first-chunk
          if (firstEvent) {
            firstEvent = false;
            this.emit('metric', { name: 'ttfc', value: Date.now() - startedAt });
          }

          // Emit the event to the React layer
          this.emit('events', [event]);

          // Open per-message stream if chunks are present and not complete
          if (
            event.kind === 'message' &&
            event.id &&
            isMessageStreaming(event.data as Record<string, unknown>)
          ) {
            this._openPerMessageStream(sessionId, event.id);
          }
        } catch {
          // Malformed JSON — ignore silently
        }
      },

      onerror: (err) => {
        if (this.stopped) return;
        const delay = this.backoff.next();
        this.emit('metric', { name: 'reconnect', value: this.backoff['attempt'] });
        this.emit('error', `SSE list_events error, reconnecting in ${Math.round(delay)}ms: ${String(err)}`);
        this._scheduleReconnect(delay);
        // Throw to stop fetchEventSource's built-in retry (we manage it ourselves)
        throw err;
      },

      onclose: () => {
        if (this.stopped) return;
        // Server closed cleanly — reconnect after a short delay
        const delay = this.backoff.next();
        this._scheduleReconnect(delay);
      },
    }).catch(() => {
      // Errors surfaced through onerror; nothing to do here
    });
  }

  private _scheduleReconnect(delayMs: number): void {
    this._clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) this._reconnect();
    }, delayMs);
  }

  private _clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // -------------------------------------------------------------------------
  // Per-message streaming
  // -------------------------------------------------------------------------

  private _openPerMessageStream(sessionId: string, eventId: string): void {
    this.governor.request(eventId, (_id, signal) => {
      const url = `${this.serverUrl}/sessions/${sessionId}/events/${eventId}?sse=true`;
      const headers = this._headers();
      const msgBackoff = new BackoffPolicy({ baseMs: 300, maxMs: 5_000 });
      let attempts = 0;

      const attempt = (): void => {
        fetchEventSource(url, {
          headers,
          signal,

          onopen: async (response) => {
            if (response.ok) {
              msgBackoff.reset();
              return;
            }
            throw new Error(`per-message SSE open failed: ${response.status}`);
          },

          onmessage: (ev) => {
            if (!ev.data) return;
            try {
              const raw = JSON.parse(ev.data) as Record<string, unknown>;
              const updated = normalizeParlantEvent(raw);
              this.emit('events', [updated]);

              // Check for termination
              const data = updated.data as Record<string, unknown> | undefined;
              if (data?.chunks) {
                const chunks = data.chunks as (string | null)[];
                if (chunks.length > 0 && chunks[chunks.length - 1] === null) {
                  this.governor.release(eventId);
                  msgBackoff.destroy();
                }
              }
            } catch {
              // ignore
            }
          },

          onerror: (err) => {
            attempts++;
            if (attempts >= 3 || signal.aborted) {
              // Give up: mark message as interrupted
              this.emit('events', [{
                id: eventId,
                kind: 'message',
                source: 'ai_agent',
                offset: -1,
                creation_utc: new Date().toISOString(),
                data: { message: '', _interrupted: true },
              } as unknown as ParlantEvent]);
              this.governor.release(eventId);
              msgBackoff.destroy();
              throw err; // stop fetchEventSource
            }
            const delay = msgBackoff.next();
            return delay;
          },

          onclose: () => {
            this.governor.release(eventId);
            msgBackoff.destroy();
          },
        }).catch(() => {
          this.governor.release(eventId);
          msgBackoff.destroy();
        });
      };

      attempt();
    });
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  private _listEventsUrl(sessionId: string, minOffset: number): string {
    return (
      `${this.serverUrl}/sessions/${sessionId}/events` +
      `?sse=true&min_offset=${minOffset}&wait_for_data=${this.waitForData}`
    );
  }

  private _headers(): Record<string, string> {
    const h: Record<string, string> = { Accept: 'text/event-stream' };
    if (this.authToken) h['Authorization'] = `Bearer ${this.authToken}`;
    return h;
  }
}
